import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'https://clicksaver.app';

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  .auth-root { font-family: 'Inter', sans-serif; min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #f4f6f9; padding: 24px; }
  .auth-card { background: #fff; border-radius: 16px; box-shadow: 0 4px 24px rgba(0,0,0,0.08); padding: 48px 40px; width: 100%; max-width: 420px; }
  .auth-label { display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 6px; }
  .auth-input { width: 100%; padding: 11px 14px; border-radius: 10px; border: 1.5px solid #e2e8f0; font-size: 14px; font-family: 'Inter', sans-serif; color: #0f172a; background: #fff; outline: none; transition: border-color .2s, box-shadow .2s; }
  .auth-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
  .auth-btn { width: 100%; padding: 13px; border-radius: 10px; border: none; cursor: pointer; background: #2563eb; color: #fff; font-size: 15px; font-weight: 600; font-family: 'Inter', sans-serif; transition: all .2s; }
  .auth-btn:hover:not(:disabled) { background: #1d4ed8; }
  .auth-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .auth-error { padding: 11px 14px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 10px; color: #dc2626; font-size: 13px; }
  .auth-success { padding: 11px 14px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; color: #059669; font-size: 13px; }
`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) return setError('Passwords do not match.');
    if (password.length < 6) return setError('Password must be at least 6 characters.');
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Reset failed. The link may have expired.');
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="auth-root">
        <style>{CSS}</style>
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Invalid reset link</h2>
          <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>This link is missing a token. Please request a new password reset.</p>
          <Link to="/login" style={{ color: '#2563eb', fontSize: 14, fontWeight: 600 }}>← Back to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-root">
      <style>{CSS}</style>
      <div className="auth-card">
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #2563eb, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#fff', fontWeight: 800 }}>⊛</div>
            <span style={{ fontWeight: 800, fontSize: 18, color: '#0f172a' }}>ClickSaver</span>
          </Link>
        </div>

        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>Choose a new password</h1>
        <p style={{ fontSize: 14, color: '#64748b', marginBottom: 28 }}>Enter a new password for your account.</p>

        {done ? (
          <div className="auth-success" style={{ textAlign: 'center' }}>
            ✓ Password updated! Redirecting to sign in...
          </div>
        ) : (
          <form onSubmit={handle} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label className="auth-label">New Password</label>
              <input className="auth-input" type="password" placeholder="Min. 6 characters"
                value={password} onChange={e => setPassword(e.target.value)} required autoFocus />
            </div>
            <div>
              <label className="auth-label">Confirm Password</label>
              <input className="auth-input" type="password" placeholder="••••••••"
                value={confirm} onChange={e => setConfirm(e.target.value)} required />
            </div>
            {error && <div className="auth-error">{error}</div>}
            <button className="auth-btn" type="submit" disabled={loading}>
              {loading ? 'Updating…' : 'Set New Password →'}
            </button>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Link to="/login" style={{ fontSize: 13, color: '#64748b', textDecoration: 'none' }}>← Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
